/**
 * Image preloading hook
 * 
 * Manages preloading of provider images with persistent caching.
 * Uses an in-memory cache to avoid reloading images when component remounts
 * or when user switches tabs and comes back.
 */

import { useState, useEffect, useRef } from 'react';
import type { MappedMatch } from '../types/matchmaking.types';
import { preloadProviderImages } from '../utils/image-preloading.utils';
import { useLoadingMessages } from './useLoadingMessages';
import {
  getAllCachedImages,
  setCachedImages,
  getCachedImage,
} from '../utils/image-cache.utils';

export const useImagePreloading = (
  apiResults: MappedMatch[],
  isLoading: boolean,
) => {
  // Initialize with cached images from previous session
  const [isPreloadingImages, setIsPreloadingImages] = useState(false);
  const [preloadedImages, setPreloadedImages] = useState<Record<string, string | null>>(() => 
    getAllCachedImages()
  );
  const hasInitializedRef = useRef(false);
  const { currentLoadingMessageIndex: imagePreloadingMessageIndex, currentLoadingTheme: currentImagePreloadingTheme } = useLoadingMessages(isPreloadingImages);

  // Initialize with cached images on first render
  useEffect(() => {
    if (!hasInitializedRef.current) {
      const cachedImages = getAllCachedImages();
      if (Object.keys(cachedImages).length > 0) {
        setPreloadedImages(cachedImages);
      }
      hasInitializedRef.current = true;
    }
  }, []);

  // Preload all provider images when matches are available
  useEffect(() => {
    const preloadImages = async () => {
      if (apiResults.length === 0 || isLoading || isPreloadingImages) {
        // Reset images when no results
        if (apiResults.length === 0) {
          setPreloadedImages({});
          return;
        }
        return;
      }

      // Check which images need to be preloaded (not in cache)
      const providerIds = apiResults
        .map((match) => match.providerProfile?.provider_id)
        .filter((id): id is string => Boolean(id));
      
      const uncachedIds = providerIds.filter((id) => !getCachedImage(id));
      
      // If all images are cached, use cached images and skip preloading
      if (uncachedIds.length === 0 && providerIds.length > 0) {
        const cachedImages: Record<string, string | null> = {};
        providerIds.forEach((id) => {
          const cached = getCachedImage(id);
          if (cached !== undefined) {
            cachedImages[id] = cached;
          }
        });
        setPreloadedImages((prev) => ({ ...prev, ...cachedImages }));
        return;
      }

      // Only preload if there are uncached images
      if (uncachedIds.length === 0) {
        return;
      }

      setIsPreloadingImages(true);
      try {
        // Use cached images as existing images to avoid re-fetching
        const existingImages = getAllCachedImages();
        const imageMap = await preloadProviderImages(
          apiResults,
          existingImages,
        );
        
        // Update cache with newly loaded images
        setCachedImages(imageMap);
        
        // Update state with all images (cached + newly loaded)
        setPreloadedImages((prev) => ({ ...prev, ...imageMap }));
      } catch (error) {
        console.error('Error preloading images:', error);
        // Don't clear on error - keep existing cached images
      } finally {
        setIsPreloadingImages(false);
      }
    };

    preloadImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiResults, isLoading]);

  return {
    isPreloadingImages,
    preloadedImages,
    setPreloadedImages,
    setIsPreloadingImages,
    imagePreloadingMessageIndex,
    currentImagePreloadingTheme,
  };
};

